import React from 'react';
import { Drawer, Button, Typography, Empty, Divider, notification } from 'antd';
import { BulbOutlined, WarningOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';

const { Text } = Typography;

import {
	ConflictScenario as ReduxConflictScenario,
	PromptImprovement as ReduxPromptImprovement,
} from '@/store/workflowsAnalysis/slice';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getUser, updateUser } from '@/api/user';
import {
	acceptImprovement,
	rejectImprovement,
	removeConflictWorkflow,
} from '@/api/workflowAnalysis';
import { fetchAndUpdateWorkflowAnalysis } from '@/utils/analysisWorkflow';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgentId } from '@/store/account/selector';
import {
	IntakeDescriptionType,
	ScenarioProperty,
	WorkflowType,
} from 'src/enums/EWorkflowsAnalysis';
import { safeJSONParse } from '@/utils/helper';

interface WorkflowEnhancerProps {
	conflicts: ReduxConflictScenario[];
	improvements: ReduxPromptImprovement[];
	onApplyImprovement: (
		workflowId: string,
		scenarioIndex: number,
		newDescription: string,
		workflowType: string,
	) => void;
	onRejectImprovement: (workflowId: string, scenarioIndex: number) => void;
	onRemoveConflict: (
		scenarioDescription: string,
		conflictingWorkflowId: string,
		conflictingScenarioIndex: number,
	) => void;
	drawerVisible: boolean;
	activeTab: 'conflicts' | 'improvements';
	onClose: () => void;
	dismissedConflicts?: Set<string>;
	dismissedImprovements?: Set<string>;
	onDismissConflict?: (conflictId: string) => void;
	onDismissImprovement?: (improvementId: string) => void;
}

const WorkflowEnhancer: React.FC<WorkflowEnhancerProps> = ({
	conflicts = [],
	improvements = [],
	onApplyImprovement,
	onRejectImprovement,
	onRemoveConflict,
	drawerVisible,
	activeTab,
	onClose,
	dismissedConflicts = new Set(),
	dismissedImprovements = new Set(),
	onDismissConflict = () => {},
	onDismissImprovement = () => {},
}) => {
	const dispatch = useDispatch();
	const safeImprovements = React.useMemo(() => improvements || [], [improvements]);
	const selectedAgentId = useAppSelector(getSelectedAgentId);

	const safeConflicts = React.useMemo(
		() =>
			conflicts
				? conflicts.filter(
						(conflict) => conflict.similarPrompts && conflict.similarPrompts.length > 0,
					)
				: [],
		[conflicts],
	);

	const visibleConflicts = safeConflicts.filter(
		(_, index) => !dismissedConflicts.has(`conflict-${index}`),
	);
	const visibleImprovements = safeImprovements.filter(
		(improvement) =>
			!dismissedImprovements.has(`${improvement.workflowId}-${improvement.scenarioIndex}`),
	);

	const { data: loggedInUser, refetch } = useQuery({
		queryKey: ['receptionist'],
		queryFn: () => getUser(selectedAgentId),
		initialData: {} as any,
	});

	const updateMutation = useMutation({
		mutationKey: ['update-user'],
		mutationFn: updateUser,
		onSuccess: () => {
			notification.success({
				message: 'Workflow updated successfully',
			});
			refetch();
		},
		onError: (error: any) => {
			const responseMessage = error?.response?.data?.message || 'Please try again later';

			notification.error({
				message: 'Failed to update workflow',
				description: responseMessage,
			});
		},
	});

	const acceptMutation = useMutation({
		mutationKey: ['accept-improvement'],
		mutationFn: acceptImprovement,
		onSuccess: async () => {
			refetch();
		},
		onError: (error: any) => {
			notification.error({
				message: 'Failed to accept improvement',
				description: error?.message || 'Please try again later',
			});
		},
	});

	const rejectMutation = useMutation({
		mutationKey: ['reject-improvement'],
		mutationFn: rejectImprovement,
		onSuccess: async () => {
			refetch();
		},
		onError: (error: any) => {
			notification.error({
				message: 'Failed to reject improvement',
				description: error?.message || 'Please try again later',
			});
		},
	});

	const removeConflictMutation = useMutation({
		mutationKey: ['remove-conflict'],
		mutationFn: removeConflictWorkflow,
		onSuccess: async () => {
			refetch();
			await fetchAndUpdateWorkflowAnalysis(dispatch, selectedAgentId);
		},
		onError: (error: any) => {
			notification.error({
				message: 'Failed to remove conflict',
				description: error?.message || 'Please try again later',
			});
		},
	});

	const handleAcceptImprovement = async (
		workflowId: string,
		scenarioIndex: number,
		enhancedDescription: string,
		workflowType: string,
	) => {
		try {
			await acceptMutation.mutateAsync({
				analysisId: workflowId,
				scenarioId: String(scenarioIndex),
				enhancedDescription,
				workflowType,
				agentId: selectedAgentId,
			});

			if (!loggedInUser) {
				return;
			}

			let userWorkflows = loggedInUser[workflowType];

			if (userWorkflows && typeof userWorkflows === 'string') {
				try {
					const firstParse = safeJSONParse(userWorkflows, []);
					if (typeof firstParse === 'string') {
						try {
							userWorkflows = safeJSONParse(firstParse, []);
						} catch {
							userWorkflows = firstParse;
						}
					} else {
						userWorkflows = firstParse;
					}
				} catch (error) {
					notification.error({
						message: error.message || 'No valid workflows found',
						description: 'Please check your workflows configuration',
					});
					userWorkflows = [];
				}
			}

			if (!Array.isArray(userWorkflows) || userWorkflows.length === 0) {
				return;
			}

			const updatedWorkflows = userWorkflows.map((workflow: any) => {
				if (workflow.id === workflowId) {
					const updatedWorkflow = { ...workflow };

					if (ScenarioProperty.CUSTOM_SCENARIO in updatedWorkflow) {
						updatedWorkflow.customScenario = enhancedDescription;
					} else if (ScenarioProperty.SCENARIO in updatedWorkflow) {
						updatedWorkflow.scenario = enhancedDescription;
					} else {
						updatedWorkflow.description = enhancedDescription;
					}

					return updatedWorkflow;
				}

				return workflow;
			});

			switch (workflowType) {
				case WorkflowType.INTAKE_QUESTIONS: {
					let intake_questions = loggedInUser.intake_questions;

					if (typeof intake_questions === 'string') {
						intake_questions = safeJSONParse(intake_questions, []);
					}

					const beginningWorkflow = {
						customScenario: '',
						description: IntakeDescriptionType.BEGINNING,
						questions: intake_questions,
						intake_beginning_natural: true,
					};

					const updatedFormBuilders = updatedWorkflows.map((workflow) => {
						if (workflow.customScenario && !workflow.description) {
							return { ...workflow, description: IntakeDescriptionType.CUSTOM_SCENARIO };
						}
						return workflow;
					});

					const finalFormBuilders = [beginningWorkflow, ...updatedFormBuilders];
					await updateMutation.mutateAsync({
						agentId: selectedAgentId,
						formBuilders: finalFormBuilders,
					});
					break;
				}
				case WorkflowType.CALL_WEBHOOK:
					await updateMutation.mutateAsync({
						agentId: selectedAgentId,
						[workflowType]: JSON.stringify(updatedWorkflows),
					});
					break;

				case WorkflowType.CALENDAR:
					break;

				default:
					await updateMutation.mutateAsync({
						agentId: selectedAgentId,
						[workflowType]: updatedWorkflows,
					});
					break;
			}

			onApplyImprovement(workflowId, scenarioIndex, enhancedDescription, workflowType);
		} catch (error) {
			notification.error({
				message: error.message || 'Error accepting improvement',
				description: 'An unexpected error occurred while accepting the improvement',
			});
		}
	};

	const handleRejectImprovement = async (workflowId: string, scenarioIndex: number) => {
		try {
			await rejectMutation.mutateAsync({
				analysisId: workflowId,
				scenarioId: String(scenarioIndex),
				agentId: selectedAgentId as string,
			});

			if (visibleImprovements.length === 1) {
				onClose();
			}
			onRejectImprovement(workflowId, scenarioIndex);
		} catch (error) {
			notification.error({
				message: error.message || 'Error rejecting improvement',
				description: 'An unexpected error occurred while rejecting the improvement',
			});
		}
	};

	const handleRemoveConflict = async (
		scenarioDescription: string,
		conflictingWorkflowId: string,
		conflictingScenarioIndex: number,
		workflowName: string,
	) => {
		try {
			const workflowNameMap: Record<string, string> = {
				'Call Transfer': 'callWorkflows',
				Text: 'workflows',
				Webhook: 'call_webhook_workflows',
				Email: 'email_workflow',
				Calendar: 'calendar_workflow',
				'Intake Questions': 'intake_questions_workflows',
			};

			const normalizedWorkflowName = workflowNameMap[workflowName] || workflowName;

			await removeConflictMutation.mutateAsync({
				analysisId: conflictingWorkflowId,
				scenarioId: String(conflictingScenarioIndex),
				workflowName: normalizedWorkflowName,
				agentId: selectedAgentId as string,
			});

			if (visibleConflicts.length === 1) {
				onClose();
			}
			onRemoveConflict(scenarioDescription, conflictingWorkflowId, conflictingScenarioIndex);
		} catch (error) {
			notification.error({
				message: error.message || 'Error removing conflict',
				description: 'An unexpected error occurred while removing the conflict',
			});
		}
	};

	const handleDismissConflict = (index: number) => {
		onDismissConflict(`conflict-${index}`);
	};

	const handleDismissImprovement = (workflowId: string, scenarioIndex: number) => {
		onDismissImprovement(`${workflowId}-${scenarioIndex}`);
	};

	return (
		<Drawer
			title={
				<div className="flex items-center">
					{activeTab === 'conflicts' ? (
						<>
							<WarningOutlined className="mr-2 text-red-500" />
							<span>Overlapping Scenarios ({visibleConflicts.length})</span>
						</>
					) : (
						<>
							<BulbOutlined className="mr-2 text-blue-500" />
							<span>Suggested Improvements ({visibleImprovements.length})</span>
						</>
					)}
				</div>
			}
			placement="right"
			onClose={onClose}
			open={drawerVisible}
			width={480}
			bodyStyle={{ padding: '16px', overflow: 'auto' }}
			destroyOnClose={true}
		>
			{activeTab === 'conflicts' ? (
				<ConflictsList
					conflicts={visibleConflicts}
					onRemoveConflict={handleRemoveConflict}
					onDismiss={handleDismissConflict}
					originalConflicts={safeConflicts}
				/>
			) : (
				<ImprovementsList
					improvements={visibleImprovements}
					handleAcceptImprovement={handleAcceptImprovement}
					handleRejectImprovement={handleRejectImprovement}
					onDismiss={handleDismissImprovement}
				/>
			)}
		</Drawer>
	);
};

const ConflictsList: React.FC<{
	conflicts: ReduxConflictScenario[];
	originalConflicts: ReduxConflictScenario[];
	onRemoveConflict: (
		scenarioDescription: string,
		conflictingWorkflowId: string,
		conflictingScenarioIndex: number,
		workflowName: string,
	) => void;
	onDismiss: (index: number) => void;
}> = ({ conflicts, originalConflicts, onRemoveConflict, onDismiss }) => {
	const [loadingItems, setLoadingItems] = React.useState<{ [key: string]: boolean }>({});

	const handleRemoveConflict = async (
		scenarioDescription: string,
		conflictingWorkflowId: string,
		conflictingScenarioIndex: number,
		workflowName: string,
		conflictIndex: number,
		similarIndex: number,
	) => {
		const buttonKey = `${conflictIndex}-${similarIndex}`;

		setLoadingItems((prev) => ({
			...prev,
			[buttonKey]: true,
		}));

		try {
			await onRemoveConflict(
				scenarioDescription,
				conflictingWorkflowId,
				conflictingScenarioIndex,
				workflowName,
			);
		} finally {
			setLoadingItems((prev) => ({
				...prev,
				[buttonKey]: false,
			}));
		}
	};

	if (!conflicts || conflicts.length === 0) {
		return <Empty description="No overlaps found" />;
	}

	return (
		<div>
			<div className="bg-yellow-50 p-3 mb-4 rounded border border-yellow-200">
				<Text>
					We found {originalConflicts.length} scenario{originalConflicts.length !== 1 ? 's' : ''}{' '}
					that might be confusing for your AI assistant. Fixing these will help it respond correctly
					each time.
				</Text>
			</div>

			{conflicts.map((conflict) => {
				const originalIndex = originalConflicts.findIndex(
					(original) =>
						original.scenarioDescription === conflict.scenarioDescription &&
						JSON.stringify(original.similarPrompts) === JSON.stringify(conflict.similarPrompts),
				);

				return (
					<div
						key={`conflict-${originalIndex}`}
						className="mb-6 bg-white border border-gray-200 rounded p-4 relative"
					>
						<Button
							type="text"
							size="small"
							icon={<CloseOutlined />}
							className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
							onClick={() => onDismiss(originalIndex)}
							title="Dismiss temporarily"
						/>

						<Text strong className="block mb-2 pr-8">
							Original scenario:
						</Text>
						<div className="bg-gray-50 p-3 rounded mb-4 border-l-4 border-blue-400">
							{conflict.scenarioDescription}
						</div>

						<Divider plain className="mb-3">
							<Text type="secondary">Similar scenarios found</Text>
						</Divider>

						{conflict.similarPrompts &&
							conflict.similarPrompts.map((similar, i) => {
								const buttonKey = `${originalIndex}-${i}`;
								const isLoading = loadingItems[buttonKey] || false;

								return (
									<div
										key={`similar-${originalIndex}-${i}`}
										className="mb-4 bg-gray-50 p-3 rounded border"
									>
										<div className="flex justify-between items-center mb-2">
											<Text className="font-semibold">{similar.workflowName}</Text>
											<Text type="secondary">Scenario #{similar.scenarioIndex + 1}</Text>
										</div>

										<div className="mb-3">{similar.description}</div>

										<div className="text-right">
											<Button
												danger
												size="small"
												loading={isLoading}
												onClick={() =>
													handleRemoveConflict(
														conflict.scenarioDescription,
														similar.workflowId,
														similar.scenarioIndex,
														similar.workflowName,
														originalIndex,
														i,
													)
												}
											>
												Remove
											</Button>
										</div>
									</div>
								);
							})}
					</div>
				);
			})}
		</div>
	);
};

interface ImprovementsListProps {
	improvements: ReduxPromptImprovement[];
	handleAcceptImprovement: (
		workflowId: string,
		scenarioIndex: number,
		enhancedDescription: string,
		workflowType: string,
	) => Promise<void>;
	handleRejectImprovement: (workflowId: string, scenarioIndex: number) => Promise<void>;
	onDismiss: (workflowId: string, scenarioIndex: number) => void;
}

const ImprovementsList: React.FC<ImprovementsListProps> = ({
	improvements,
	handleAcceptImprovement,
	handleRejectImprovement,
	onDismiss,
}) => {
	const [loadingButtons, setLoadingButtons] = React.useState<{
		[key: string]: { accept: boolean; reject: boolean };
	}>({});

	const handleAccept = async (
		workflowId: string,
		scenarioIndex: number,
		enhancedDescription: string,
		workflowType: string,
	) => {
		const buttonKey = `${workflowId}-${scenarioIndex}`;

		setLoadingButtons((prev) => ({
			...prev,
			[buttonKey]: {
				...prev[buttonKey],
				accept: true,
			},
		}));

		try {
			await handleAcceptImprovement(workflowId, scenarioIndex, enhancedDescription, workflowType);
		} finally {
			setLoadingButtons((prev) => ({
				...prev,
				[buttonKey]: {
					...prev[buttonKey],
					accept: false,
				},
			}));
		}
	};

	const handleReject = async (workflowId: string, scenarioIndex: number) => {
		const buttonKey = `${workflowId}-${scenarioIndex}`;

		setLoadingButtons((prev) => ({
			...prev,
			[buttonKey]: {
				...prev[buttonKey],
				reject: true,
			},
		}));

		try {
			await handleRejectImprovement(workflowId, scenarioIndex);
		} finally {
			setLoadingButtons((prev) => ({
				...prev,
				[buttonKey]: {
					...prev[buttonKey],
					reject: false,
				},
			}));
		}
	};

	if (!improvements || improvements.length === 0) {
		return <Empty description="No improvements available" />;
	}

	return (
		<div>
			<div className="bg-blue-50 p-3 mb-4 rounded border border-blue-200">
				<Text>Here are some ways to make your scenarios clearer for your AI assistant.</Text>
			</div>

			{improvements.map((improvement) => {
				const buttonKey = `${improvement.workflowId}-${improvement.scenarioIndex}`;
				const isAcceptLoading = loadingButtons[buttonKey]?.accept || false;
				const isRejectLoading = loadingButtons[buttonKey]?.reject || false;

				return (
					<div
						key={`improvement-${improvement.workflowId}-${improvement.scenarioIndex}`}
						className="mb-6 bg-white border border-gray-200 rounded p-4 relative"
					>
						<Button
							type="text"
							size="small"
							icon={<CloseOutlined />}
							className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
							onClick={() => onDismiss(improvement.workflowId, improvement.scenarioIndex)}
							title="Dismiss temporarily"
						/>

						<div className="flex justify-between items-center mb-3 pr-8">
							<Text strong>{improvement.workflowName}</Text>
							<Text type="secondary">Scenario #{improvement.scenarioIndex + 1}</Text>
						</div>

						<div className="mb-4">
							<Text className="block mb-1">Current wording:</Text>
							<div className="bg-gray-50 p-3 rounded">{improvement.currentDescription}</div>
						</div>

						<div className="mb-4">
							<Text className="block mb-1">Suggested better wording:</Text>
							<div className="bg-green-50 p-3 rounded border-l-4 border-green-400">
								{improvement.suggestedDescription}
							</div>
						</div>

						<div className="flex justify-end gap-2">
							<Button
								danger
								size="small"
								icon={<CloseOutlined />}
								loading={isRejectLoading}
								onClick={(e) => {
									e.stopPropagation();
									handleReject(improvement.workflowId, improvement.scenarioIndex);
								}}
							>
								Reject
							</Button>
							<Button
								type="primary"
								size="small"
								icon={<CheckOutlined />}
								loading={isAcceptLoading}
								onClick={(e) => {
									e.stopPropagation();
									handleAccept(
										improvement.workflowId,
										improvement.scenarioIndex,
										improvement.suggestedDescription,
										improvement.workflowType,
									);
								}}
							>
								Accept
							</Button>
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default WorkflowEnhancer;
